import { Signer } from '@ethersproject/abstract-signer'
import { Provider } from '@ethersproject/providers'
import { assertEx, EthAddress, forget } from '@xylabs/sdk-js'
import { TypedEvent } from '@xyo-network/world-typechain'

import { XyoWorldBase } from '../../Base'
import {
  XyoErc20Wrapper,
  XyoWorldGeotokensWrapper,
  XyoWorldMinterVersion1Wrapper,
  XyoWorldMinterVersion2Wrapper,
  XyoWorldMinterWrapper,
} from '../../EthersClients'
import { BaseJsonObject } from '../../models'
import { Quadkey } from '../../Quadkey'
import { XyoNetworkWrapper } from '../../wrappers'
import { AuctionStatus, XyoWorldAuction } from './Auction'
import { AuctionFactoryJsonObject, XyoWorldAuctionFactory } from './AuctionFactory'
import { MinterDeployBlocks } from './DeployBlocks'
import { MinterEvents, MinterEventsJsonObject, quadkeysFromMinterEvent } from './Events'
import { MinterContracts } from './MinterContracts'

export interface XyoWorldMinterJsonObject extends BaseJsonObject {
  auctions: AuctionFactoryJsonObject
  blockNumber?: number
  events?: MinterEventsJsonObject
}

export class XyoWorldMinter extends XyoWorldBase<XyoWorldMinterJsonObject> {
  static minter?: XyoWorldMinter

  public blockNumber?: number

  public contracts: MinterContracts

  public provider: Provider

  public secondsPerBlock = 13.37

  public signer?: Signer

  private _address?: EthAddress

  private _auctions?: XyoWorldAuctionFactory

  private _currency?: XyoErc20Wrapper

  private _events?: MinterEvents

  private _geotokens?: XyoWorldGeotokensWrapper

  private constructor(network: XyoNetworkWrapper, provider: Provider, signer?: Signer, json?: XyoWorldMinterJsonObject) {
    super()
    const currentMinterAddress = assertEx(network.addresses.minter, 'Minter Address Required')
    this.provider = provider
    this.signer = signer
    this.contracts = {
      current: new XyoWorldMinterWrapper(currentMinterAddress, provider, signer),
      v1: network.addresses?.minter_v1 ? new XyoWorldMinterVersion1Wrapper(network.addresses?.minter_v1, provider, signer) : undefined,
      v2: network.addresses?.minter_v2 ? new XyoWorldMinterVersion2Wrapper(network.addresses?.minter_v2, provider, signer) : undefined,
    }
    this.loadJsonObject(json)
    this.events().monitorEvents((event) => {
      forget(this.updateWithEvents([event]))
    })
    // this.calibrateTime()
  }

  static get(network: XyoNetworkWrapper, provider: Provider, signer?: Signer, jsonObject?: XyoWorldMinterJsonObject) {
    this.minter = new XyoWorldMinter(network, provider, signer, jsonObject)
    return this.minter
  }

  public address() {
    this._address = this._address || assertEx(EthAddress.fromString(this.contracts.current.contract.address))
    return this._address
  }

  public auctions(): XyoWorldAuctionFactory {
    this._auctions = this._auctions || new XyoWorldAuctionFactory(this.contracts)
    return this._auctions
  }

  public async calibrateTime() {
    try {
      const currentBlockNumber = (await this.contracts.current.contract.provider.getBlockNumber()) - 10
      const sampleBlockNumber = MinterDeployBlocks.v3
      const sampleBlock = await this.contracts.current.contract.provider.getBlock(sampleBlockNumber)
      const sampleBlockTime = sampleBlock.timestamp
      const currentBlock = await this.contracts.current.contract.provider.getBlock(currentBlockNumber)
      const currentBlockTime = currentBlock.timestamp
      const totalBlocks = currentBlockNumber - sampleBlockNumber
      const totalTime = currentBlockTime - sampleBlockTime
      const avgBlockTime = totalTime / totalBlocks
      console.log(`=>>>>> calibrateTime:deployedBlockTime: ${sampleBlockTime}`)
      console.log(`=>>>>> calibrateTime:avgBlockTime: ${avgBlockTime}`)
      this.secondsPerBlock = avgBlockTime
      return avgBlockTime
    } catch (ex) {
      console.warn('Could not Calibrate Time')
      console.warn(ex)
    }
  }

  public clear() {
    this.auctions().clear()
    this.blockNumber = undefined
    return this
  }

  public async currency() {
    this._currency = this._currency || (await this.contracts.current.getCurrency())
    return this._currency
  }

  public events(): MinterEvents {
    this._events = this._events || new MinterEvents(this.contracts)
    return this._events
  }

  public async geotokens() {
    this._geotokens = this._geotokens || (await this.contracts.current.getTokens())
    return this._geotokens
  }

  public async getNow() {
    return await this.contracts.current.getNow()
  }

  public async listAuctions(filter: AuctionStatus) {
    const result: XyoWorldAuction[] = []

    switch (filter) {
      case AuctionStatus.Available:
        await this.addAvailableAuctionChildren(this.auctions().get(Quadkey.root), result)
        break
      case AuctionStatus.Live:
        await this.addLiveAuctionChildren(this.auctions().get(Quadkey.root), result)
        break
      case AuctionStatus.Dutch:
        await this.addDutchAuctionChildren(this.auctions().get(Quadkey.root), result)
        break
      case AuctionStatus.Completed:
        await this.addCompletedAuctionChildren(this.auctions().get(Quadkey.root), result)
        break
      case AuctionStatus.Upcoming:
        await this.addUpcomingAuctionChildren(this.auctions().get(Quadkey.root), result)
        break
      case AuctionStatus.Minted:
        await this.addMintedAuctionChildren(this.auctions().get(Quadkey.root), result)
        break
    }
    return result
  }

  public loadJsonObject(jsonObject?: XyoWorldMinterJsonObject) {
    if (jsonObject) {
      this.blockNumber = jsonObject.blockNumber
      this.auctions().loadJsonObject(jsonObject.auctions)
    }
    return this
  }

  public now() {
    return this.contracts.current.now()
  }

  public async refresh() {
    this.clear()
    await this.updateWithAllEvents()
    return this
  }

  public toJsonObject(): XyoWorldMinterJsonObject {
    return {
      auctions: this.auctions().toJsonObject(),
      blockNumber: this.blockNumber,
    }
  }

  public toJsonObjectWithEvents(): XyoWorldMinterJsonObject {
    return {
      events: this.events().toJsonObject(),
      ...this.toJsonObject(),
    }
  }

  public async updateWithAllEvents(maxBlocksPerRead = 200000) {
    this.blockNumber = MinterDeployBlocks.v1
    return await this.updateWithNewEvents(maxBlocksPerRead)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async updateWithEvents(events: TypedEvent<any, any>[]) {
    console.log(`updateWithEvents: ${events.length}`)
    this.blockNumber = this.blockNumber ?? MinterDeployBlocks.v1
    const eventsByQuadkey = new Map<string, TypedEvent[]>()
    for (const event of events) {
      const quadkeys = quadkeysFromMinterEvent(event)
      for (const quadkey of quadkeys) {
        let eventList = eventsByQuadkey.get(quadkey.toBase4HashLabel())
        if (!eventList) {
          eventList = []
          eventsByQuadkey.set(quadkey.toBase4HashLabel(), eventList)
        }
        eventList.push(event)
      }
      // update the blockNumber so if updateWithNewEvents is called, it knows where to start
      if (this.blockNumber && event.blockNumber > this.blockNumber) {
        this.blockNumber = event.blockNumber
      }
    }

    const promises: Promise<XyoWorldAuction>[] = []

    for (const pair of eventsByQuadkey) {
      const quadkey = Quadkey.fromBase4String(pair[0])
      if (quadkey) {
        const events = pair[1]
        if (events.length > 0) {
          promises.push(this.auctions().get(quadkey).updateWithEvents(events))
        }
      }
    }
    await Promise.all(promises)
    return this
  }

  public async updateWithNewEvents(maxBlocksPerRead = 200000) {
    const currentBlockNumber = await this.provider.getBlockNumber()
    let storedBlockNumber = this.blockNumber ?? MinterDeployBlocks.v1
    while (storedBlockNumber < currentBlockNumber) {
      let toBlock = storedBlockNumber + maxBlocksPerRead
      if (toBlock > currentBlockNumber) {
        toBlock = currentBlockNumber
      }
      const events = await this.events().all(storedBlockNumber, toBlock)
      await this.updateWithEvents(events)
      storedBlockNumber = toBlock
    }
    this.blockNumber = currentBlockNumber
    return this
  }

  private async addAvailableAuctionChildren(auction: XyoWorldAuction, auctions: XyoWorldAuction[]) {
    const children = auction.quadkey.children()
    const childAuctions: XyoWorldAuction[] = []
    for (const child of children) {
      const childAuction = this.auctions().get(child)
      childAuctions.push(childAuction)
    }
    const childrenPromises: Promise<void>[] = []
    for (const childAuction of childAuctions) {
      if (childAuction.static?.minted) {
        childrenPromises.push(this.addAvailableAuctionChildren(childAuction, auctions))
      } else if (childAuction.isLive() || childAuction.isDutch()) {
        auctions.push(childAuction)
      }
    }
    await Promise.all(childrenPromises)
  }

  private async addCompletedAuctionChildren(auction: XyoWorldAuction, auctions: XyoWorldAuction[]) {
    const children = auction.quadkey.children()
    const childrenPromises: Promise<void>[] = []
    for (const child of children) {
      const childAuction = this.auctions().get(child)
      if (childAuction.static?.minted) {
        childrenPromises.push(this.addCompletedAuctionChildren(childAuction, auctions))
      } else if (childAuction.isCompleted()) {
        auctions.push(childAuction)
      }
    }
    await Promise.all(childrenPromises)
  }

  private async addDutchAuctionChildren(auction: XyoWorldAuction, auctions: XyoWorldAuction[]) {
    const children = auction.quadkey.children()
    const childrenPromises: Promise<void>[] = []
    for (const child of children) {
      const childAuction = this.auctions().get(child)
      if (childAuction.static?.minted) {
        childrenPromises.push(this.addDutchAuctionChildren(childAuction, auctions))
      } else if (childAuction.isDutch()) {
        auctions.push(childAuction)
      }
    }
    await Promise.all(childrenPromises)
  }

  private async addLiveAuctionChildren(auction: XyoWorldAuction, auctions: XyoWorldAuction[]) {
    const children = auction.quadkey.children()
    const childAuctions: XyoWorldAuction[] = []
    for (const child of children) {
      const childAuction = this.auctions().get(child)
      childAuctions.push(childAuction)
    }
    const childrenPromises: Promise<void>[] = []
    for (const childAuction of childAuctions) {
      if (childAuction.static?.minted) {
        childrenPromises.push(this.addLiveAuctionChildren(childAuction, auctions))
      } else if (childAuction.isLive()) {
        auctions.push(childAuction)
      }
    }
    await Promise.all(childrenPromises)
  }

  private async addMintedAuctionChildren(auction: XyoWorldAuction, auctions: XyoWorldAuction[]) {
    const children = auction.quadkey.children()
    const childrenPromises: Promise<void>[] = []
    for (const child of children) {
      const childAuction = this.auctions().get(child)
      if (childAuction.static?.minted) {
        auctions.push(childAuction)
        childrenPromises.push(this.addMintedAuctionChildren(childAuction, auctions))
      }
    }
    await Promise.all(childrenPromises)
  }

  private async addUpcomingAuctionChildren(_auction: XyoWorldAuction, _auctions: XyoWorldAuction[]) {
    const children = _auction.quadkey.children()
    const childrenPromises: Promise<void>[] = []
    for (const child of children) {
      const childAuction = this.auctions().get(child)
      if (childAuction.static?.minted) {
        childrenPromises.push(this.addUpcomingAuctionChildren(childAuction, _auctions))
      } else {
        _auctions.push(childAuction)
      }
    }
    await Promise.all(childrenPromises)
  }
}
